import type { IngredientId } from '../brandedTypes/ingredientId';
import type { Coordinate, Dictionary, Money } from '../general';

export enum TransportType {
  None = 'None',
  Flight = 'Flight',
  Ferry = 'Ferry',
  Car = 'Car',
  Walk = 'Walk',
  Bicycle = 'Bicycle',
}

export type BookableTransportType = TransportType.Ferry | TransportType.Flight;
export type OwnTransportType =
  | TransportType.Car
  | TransportType.Walk
  | TransportType.Bicycle
  | TransportType.None;

export function isBookableTransportType(
  transportType: TransportType
): transportType is BookableTransportType {
  return (
    transportType === TransportType.Flight ||
    transportType === TransportType.Ferry
  );
}

export function isOwnTransportType(
  transportType: TransportType
): transportType is OwnTransportType {
  return (
    transportType === TransportType.Car ||
    transportType === TransportType.Bicycle ||
    transportType === TransportType.Walk ||
    transportType === TransportType.None
  );
}

export interface PortPosition extends Coordinate {
  name?: string;
}

export interface PortStaticInfo {
  code: string;
  position: PortPosition;
  name?: string;
}

export interface PortInfo {
  code?: string;
  name?: string;
  label?: string;
}

export interface SeaportStaticInfo {
  type: 'harbour';
  address: string;
  code: string;
  label: string;
  latitude: number;
  longitude: number;
}

export interface AirportStaticInfo {
  type: 'airport';
  code: string;
  label: string;
  latitude: number;
  longitude: number;
  popularity: number;
  city?: string;
}

export interface FerryRouteStaticInfo {
  defaultVehicleId: TransportVehicleType;
  from: FerryRouteSeaportStaticInfo;
  to: FerryRouteSeaportStaticInfo;
  allowMultipleVehicles: boolean;
  allowVehicles: boolean;
}

export interface FerryRouteSeaportStaticInfo {
  address: string;
  city: string;
  label: string;
  code: string;
  country: string;
  latitude: number;
  longitude: number;
}

export interface CarrierStaticInfo {
  code: string;
  name: string;
  logo?: string;
}

export interface TransportStaticInfo {
  ingredientId: IngredientId;
  carriers: Dictionary<CarrierStaticInfo>;
  ports: Dictionary<PortStaticInfo>;
}

export interface DestinationPoints {
  departure: PortPosition;
  arrival: PortPosition;
}

export interface TransportVehicle {
  id: TransportVehicleType;
  description: string;
}

export enum TransportVehicleType {
  Vehicle6mLong = 'Vehicle6mLong',
  MotorBike = 'MotorBike',
  Bicycle = 'Bicycle',
  MiniBus4mHigh6mLong = 'MiniBus4mHigh6mLong',
  Vehicle4m70cmLong = 'Vehicle4m70cmLong',
  MultiPurposeVehicle = 'MultiPurposeVehicle',
  MiniBus8mLong = 'MiniBus8mLong',
  None = 'None',
}

export enum TransportVehicleTypeQL {
  Vehicle6mLong = 'VEHICLE_6_M_LONG',
  MotorBike = 'MOTOR_BIKE',
  Bicycle = 'BICYCLE',
  MiniBus4mHigh6mLong = 'MINI_BUS_4_M_HIGH_6_M_LONG',
  Vehicle4m70cmLong = 'VEHICLE_4_M_70_CM_LONG',
  MultiPurposeVehicle = 'MULTI_PURPOSE_VEHICLE',
  MiniBus8mLong = 'MINI_BUS_8_M_LONG',
  None = 'NONE',
}

export interface FerryTicketCategory {
  type: string;
  description: string;
  price: Money;
  token: string;
}

export interface FerryTicketCategoryResponse {
  options: FerryTicketCategory[];
}

export type TransportDirection = 'from' | 'to';
